/* eslint-disable react/prop-types */
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Spin,
} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import FormSubmitControls from 'components/FormSubmitControls';
import Previewer from 'components/Previewer';
import Select from 'components/Select';
import Table from 'components/Table';
import Uploader from 'components/Uploader';
import React, { useEffect, useState } from 'react';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';

import { getTiposDeDocumentos } from 'api/catalogos/tiposDeDocumentos';
import { checkToken } from 'api/utils';
import { getHonorarioAsimilado } from 'api/catalogos/HonorarioAsimilado';
import {
  getDocumentoHonorarioAsimilado,
  getDocumentosHonorariosAsimiladosParams,
  patchDocumentoHonorarioAsimilado,
  postDocumentoHonorarioAsimilado,
  putDocumentosHonorarioAsimiladoStatus,
} from 'api/catalogos/documentosHonorariosAsimilados';

export const documentoEmpleadoPermission = {
  permissionModel: 'documentohonorarioasimilado',
  catalogsModels: [],
  tabsModels: [],
};

const DocumentosHonorariosAsimilados = ({
  honorarioAsimiladoId,
  rules,
  formEmpleado,
  permission,
  isBlocked,
  actualizarTablaHonorariosAsimilados,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [currentTableData, setCurrentTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [tiposDeDocumentos, setTiposDeDocumentos] = useState([]);
  const [tipoDeDocumento, setTipoDeDocumento] = useState();
  const [documentosEmpleados, setDocumentosHonorariosAsimilados] = useState([]);
  const [currentFile, setCurrentFile] = useState();
  const [visiblePreview, setVisiblePreview] = useState(false);
  // const [previewUrl, setPreviewUrl] = useState('');
  // const [isImage, setIsImage] = useState(false);
  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await
    getDocumentosHonorariosAsimiladosParams({ honorario_asimilado: honorarioAsimiladoId });
    setDocumentosHonorariosAsimilados(_data);
    setCurrentTableData([]);
    setLoading(false);
    return _data;
  };

  // El backend recalcula el estatus (Pre-Alta -> Activo) al marcar todos los
  // documentos como entregados; aquí solo reflejamos ese estatus en el form.
  const refreshEstadoHonorarioAsimilado = async () => {
    const _honorarioAsimilado = await getHonorarioAsimilado(honorarioAsimiladoId);
    if (_honorarioAsimilado && formEmpleado) {
      formEmpleado.setFieldValue(
        'estado_del_honorario_asimilado',
        _honorarioAsimilado.estado_del_honorario_asimilado,
      );
    }
    if (actualizarTablaHonorariosAsimilados) actualizarTablaHonorariosAsimilados();
  };

  useEffect(() => {
    let mounted = true;
    if (!honorarioAsimiladoId) {
      setDocumentosHonorariosAsimilados([]);
      return undefined;
    }
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _tiposDeDocumentos,
        _documentosEmpleados,
      ] = await Promise.all([
        getTiposDeDocumentos(),
        getDocumentosHonorariosAsimiladosParams({ honorario_asimilado: honorarioAsimiladoId }),
      ]);
      if (mounted) {
        setTiposDeDocumentos(_tiposDeDocumentos);
        setDocumentosHonorariosAsimilados(_documentosEmpleados);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, [honorarioAsimiladoId]);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setCurrentTableData([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || documentosEmpleados.find((item) => item.id === key);
    const _documentoEmpleado = await getDocumentoHonorarioAsimilado(match.id);
    // eslint-disable-next-line max-len
    const _tipoDeDocumento = tiposDeDocumentos.find((e) => e.id === _documentoEmpleado?.tipo_de_documento)?.nombre;
    setTipoDeDocumento(_tipoDeDocumento);
    setTimeout(() => {
      if (_documentoEmpleado) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_documentoEmpleado));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchDocumentoHonorarioAsimilado(key, values) : postDocumentoHonorarioAsimilado(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
          await refreshEstadoHonorarioAsimilado();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const onClickTrue = async () => {
    try {
      setLoading(true);
      const mappedSelectedRowKeys = selectedRowKeys.map((val) => ({
        honorario_asimilado_id: honorarioAsimiladoId, id: val, entregado: true,
      }));
      const saved = await putDocumentosHonorarioAsimiladoStatus(mappedSelectedRowKeys);
      if (saved) {
        await fetchData();
        onCancel();
      }
      await refreshEstadoHonorarioAsimilado();
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
    setLoading(false);
  };

  const onClickFalse = async () => {
    try {
      setLoading(true);
      const mappedSelectedRowKeys = selectedRowKeys.map((val) => ({
        honorario_asimilado_id: honorarioAsimiladoId, id: val, entregado: false,
      }));
      const saved = await putDocumentosHonorarioAsimiladoStatus(mappedSelectedRowKeys);
      if (saved) {
        await fetchData();
        onCancel();
      }
      await refreshEstadoHonorarioAsimilado();
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
    setLoading(false);
  };
  const onClickPreview = () => {
    const clone = [...documentosEmpleados];
    const [key] = selectedRowKeys;
    const match = clone.find((e) => e.id === key);
    setCurrentFile(match);
    setVisiblePreview(true);
  };

  const handleOnRowClick = (record) => {
    if (selectedRowKeys.includes(record.id)) {
      setSelectedRowKeys(selectedRowKeys.filter((key) => key !== record.id));
    } else {
      setSelectedRowKeys([...selectedRowKeys, record.id]);
      form.setFieldsValue(record);
    }
  };
  /*
  const handlePreview = (archivo) => {
    setPreviewUrl(archivo);
    setIsImage(archivo.includes('.jpg') || archivo.includes('.png') || archivo.includes('.jpeg'));
    setPreviewVisible(true);
  };

  const handleFileChange = () => {
    const archivo = form.getFieldValue('Documento_uploader');

    if (archivo && archivo.length > 0) {
      setDocumentosHonorariosAsimilados((prev) => ({
        ...prev,
        archivo: archivo[0], // Guardamos el primer archivo
      }));
    } else {
      setDocumentosHonorariosAsimilados((prev) => ({ ...prev, archivo: null }));
    }
  }; */

  // Dataset efectivo: prioriza los datos filtrados/paginados actuales si los hay
  const effectiveData = currentTableData.length ? currentTableData : documentosEmpleados;

  const handleTableChange = (_pagination, _filters, _sorter, extra) => {
    setCurrentTableData(extra?.currentDataSource || []);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    columnTitle: (
      <Checkbox
        checked={selectedRowKeys.length > 0 && selectedRowKeys.length === effectiveData.length}
        indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < effectiveData.length}
        onChange={(e) => {
          setSelectedRowKeys(e.target.checked ? effectiveData.map((row) => row.id) : []);
        }}
      />
    ),
    onSelect: (record, selected) => {
      if (selected) {
        setSelectedRowKeys((prev) => [...prev, record.id]);
      } else {
        setSelectedRowKeys((prev) => prev.filter((k) => k !== record.id));
      }
    },
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const esImagen = (file) => {
    if (!file || !file.archivo) return false; // Si no hay archivo, no es imagen
    const extensionRegex = /\.(jpg|jpeg|png|gif|bmp|svg|webp)$/i;
    const mimeTypeRegex = /^image\//;

    return mimeTypeRegex.test(file.type) || extensionRegex.test(file.archivo);
  };

  // eslint-disable-next-line max-len
  // const fileUrl = currentFile?.archivo ? `${currentFile.archivo}?t=${new Date().getTime()}` : null;

  const columns = [
    {
      titleText: 'Documento',
      dataIndex: 'tipo_de_documento',
      key: 'tipo_de_documento',
      width: 300,
      render: (val) => tiposDeDocumentos.find((e) => e.id === val)?.nombre,
    },
    {
      titleText: 'Tipo',
      dataIndex: 'version_del_documento',
      key: 'version_del_documento',
      width: 200,
      render: (val) => {
        if (val === 1) return 'Copia';
        if (val === 2) return 'Original';
        return 'Desconocido';
      },
    },
    {
      titleText: 'Entregado por el Empleado',
      dataIndex: 'entregado',
      key: 'entregado',
      width: 200,
      render: (val) => (val ? 'Si' : 'No'),
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 200,
    },
    /*
    {
      title: 'Archivo',
      dataIndex: 'archivo',
      key: 'archivo',
      render: (archivo) => (
        <Button type="link" onClick={() => handlePreview(archivo)}>
          Preview
        </Button>
      ),
    }, */
  ];

  const onClickDownload = () => {
    const [key] = selectedRowKeys;
    const match = documentosEmpleados.find((e) => e.id === key);

    if (match?.archivo) {
    // Usar la URL completa proporcionada por la API
      const fileUrl = match.archivo;

      // Crear un enlace de descarga y simular un click
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = match.archivo.split('/').pop(); // Extrae el nombre del archivo de la URL
      link.click();
    }
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          customActions={[
            {
              onClick: onClickTrue,
              text: 'Marcar como Entregado',
              type: 'primary',
              disabled: !selectedRowKeys.length,
            },
            {
              onClick: onClickFalse,
              text: 'Marcar como no Entregado',
              danger: true,
              disabled: !selectedRowKeys.length,
            },
            {
              onClick: onClickDownload,
              icon: <DownloadOutlined />,
              text: 'Descargar Documento',
              disabled: !selectedRowKeys.length,
            },
          ]}
          cols={columns}
          data={documentosEmpleados}
          rowSelection={rowSelection}
          onTableChange={handleTableChange}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickEdit: () => onClickEdit(),
            onClickTrue: () => onClickTrue(),
            onClickFalse: () => onClickFalse(),
          }}
          // allowMultiFalseChanger={!isBlocked}
          // allowMultiTrueChanger={!isBlocked}
          // multiFalseChangerText="Marcar como no entregados"
          // multiTrueChangerText="Marcar como entregado"
          allowDoubleClick={false}
          // permission={permission}
          allowExportPDF={false}
          permission={{
            hasAllViewPermission: true,
            add: true,
            change: true,
            delete: true,
          }}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Documento`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
          width="35%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="nombre"
                  label="Nombre de Documentos"
                >
                  <Input
                    disabled
                    defaultValue={tipoDeDocumento}
                  />
                </Form.Item>
                <Form.Item
                  name="entregado"
                  label="Entregado Por El Honorario Asimilado"
                  rules={rules.required}
                  initialValue={false}
                >
                  <Select
                    trueFalse
                    disabled={isBlocked}
                  />
                </Form.Item>
                <Uploader
                  title="Documento"
                  limitMB={5}
                  file={form.getFieldValue('archivo')}
                  formItemName="archivo"
                  allowedExtensions={['jpg', 'png', 'jpeg', 'pdf']}
                  rules={rules.required}
                  allowPreview={false}
                />

                <Form.Item
                  name="observaciones"
                  label="Observaciones"
                >
                  <TextArea
                    rows={3}
                    allowClear
                    disabled={isBlocked}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
        {/* <Previewer
          open={visiblePreview}
          setVisible={setVisiblePreview}
          iFrameUrl={fileUrl}
          isImage={esImagen(currentFile)}
        /> */}
      </Spin>
    </Row>
  );
};

export default DocumentosHonorariosAsimilados;
